import { ArrowLeft, MailCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useBranding } from '../branding/BrandingProvider.js';
import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { Input } from '../components/Input.js';
import { apiRequest } from '../lib/apiClient.js';

/**
 * Plan E5.
 *
 * **The same confirmation is rendered whatever happened**, including when the
 * request itself failed. An enumeration oracle does not care whether it reads
 * the difference from a message, a status code, or a spinner that lasts longer
 * for a real address — so this page stops distinguishing at the earliest
 * possible point, which is before the response is inspected at all.
 *
 * That extends to the *visuals*, which is why the confirmation below is styled
 * as a plain statement and not as a success: a green tick for one address and a
 * neutral note for another would leak by pixel what the copy refuses to leak by
 * word. Nothing on this page may vary with the outcome.
 */

/**
 * Matches LoginPage: the two screens a person reaches from outside the product
 * carry the same 44px control and the same typing affordance, so arriving at
 * either one feels like the same panel. The reset/change screens draw their
 * inputs from the shared `PasswordFields` block and keep 40px until that block
 * moves with them.
 */
const FIELD_CLASS = 'h-11 border-line-strong focus:border-primary';

/**
 * Whose panel this is, on a screen a person usually reaches from an email
 * rather than from inside the product. The logo when the tenant has one, the
 * product name when they do not — never a hardcoded mark, and never an
 * embedded image: `logoUrl` is whatever the branding payload says.
 */
function BrandLockup(): ReactElement {
  const { branding } = useBranding();
  return branding?.logoUrl ? (
    <img src={branding.logoUrl} alt={branding.productName} className="h-9 w-auto self-start" />
  ) : (
    <p className="text-sm font-semibold text-body">{branding?.productName ?? 'Connect Panel'}</p>
  );
}

/** The one shape both states of this page take: one card, centred, on the canvas. */
function AuthCard({ children }: { children: ReactNode }): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-alt px-[var(--space-6)] py-[var(--space-10)]">
      <div className="flex w-full max-w-md flex-col gap-[var(--space-6)] card p-[var(--space-8)] shadow-[var(--elevation-2)]">
        {children}
      </div>
    </main>
  );
}

export function ForgotPasswordPage(): ReactElement {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      await apiRequest('/auth/password/forgot', { method: 'POST', body: { email } });
    } catch {
      // Deliberately swallowed. A rate limit or a server error must not become
      // a signal the caller can use to tell one address from another.
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <AuthCard>
        <div className="flex flex-col gap-[var(--space-4)]">
          <BrandLockup />
          <span
            aria-hidden="true"
            className="flex size-10 items-center justify-center rounded-[var(--radius-md)] bg-surface-alt text-muted"
          >
            {/* Neutral, not a success tone: see the header — the mark must not
                say more than the sentence beside it is allowed to. */}
            <MailCheck size={18} strokeWidth={2} />
          </span>
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
            Check your email
          </h1>
          <p role="status" data-testid="forgot-confirmation" className="text-sm text-body">
            If that address has an account, we&rsquo;ve sent a reset link.
          </p>
        </div>
        <Link
          to="/login"
          className="inline-flex items-center gap-[var(--space-2)] self-start text-sm text-primary underline underline-offset-2"
        >
          <ArrowLeft aria-hidden="true" size={14} strokeWidth={2} />
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <div className="flex flex-col gap-[var(--space-4)]">
        <BrandLockup />
        <h1 className="text-[length:var(--text-2xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
          Reset your password
        </h1>
      </div>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex flex-col gap-[var(--space-5)]"
        noValidate
      >
        <Field label="Email address" required>
          {(props) => (
            <Input
              {...props}
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={FIELD_CLASS}
            />
          )}
        </Field>
        <Button
          type="submit"
          loading={busy}
          className="mt-[var(--space-2)] h-11 w-full font-semibold shadow-[var(--elevation-2)]"
        >
          Send reset link
        </Button>
        <Link
          to="/login"
          className="inline-flex items-center gap-[var(--space-2)] self-start text-sm text-primary underline underline-offset-2"
        >
          <ArrowLeft aria-hidden="true" size={14} strokeWidth={2} />
          Back to sign in
        </Link>
      </form>
    </AuthCard>
  );
}
